import * as React from "react";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { getStoreByOwnerIdForm } from "../../../lib/apis/dtos/request/store.request";
import { useStoreByOwnerId } from "../../../lib/apis/hooks/store.hook";
import type { matchUserForm } from "../../../lib/apis/dtos/request/user.request";
import { matchUser } from "../../../lib/apis/v1/user.service";
import { getQueryClient } from "../../../config/queryClient.config";
import ConfirmModal from "../confirm.modal";
import type { ModalComponentProps } from "../../../types/modal.types";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  style: {
    zIndex: 3000
  }
};

interface AssginBranchProps extends ModalComponentProps {
  userId: string;
  ownerId: string;
}

export default function AssignBranchModal(props: AssginBranchProps) {
  const { id, onClose, userId, ownerId } = props;

  const [branch, setBranch] = React.useState<string[]>([]);
  const [select, setSelect] = React.useState<number[]>([]);

  const form = { page: 0, size: 1000, ownerId } as getStoreByOwnerIdForm;
  const { data, isSuccess } = useQuery({
    ...useStoreByOwnerId(form),
    enabled: !!ownerId,
  });
  const { result } = data || {};
  const content = result?.content || [];

  const matchMutation = useMutation({
    mutationFn: (form: matchUserForm) => matchUser(form),
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: ["user-detail"] });
      onClose?.();
    },
  });

  const handleChange = (event: SelectChangeEvent<typeof branch>) => {
    const {
      target: { value },
    } = event;

    setBranch(typeof value === "string" ? value.split(",") : value);
  };

  return (
    <ConfirmModal
      title="담당 지점 배정"
      rightButton={{
        title: "저장",
        onClick: () => {
          matchMutation.mutate({ id: userId, branch: select });
        },
      }}
      id={id}
      onClose={onClose}
    >
      <div className="flex flex-col gap-4 mt-4">
        <p className="py-3 text-[16px] text-black/60">
          담당자가 담당할 지점을 선택해주세요. 복수 선택 가능합니다.
        </p>
        <FormControl>
          <InputLabel id="demo-multiple-chip-label">지점 배정</InputLabel>
          <Select
            labelId="demo-multiple-chip-label"
            id="demo-multiple-chip"
            className="w-[552px]"
            multiple
            value={branch}
            onChange={handleChange}
            input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
            inputProps={{ "aria-label": "지점 배정" }}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value, index) => (
                  <Chip
                    key={value}
                    onMouseDown={(e) => e.stopPropagation()}
                    label={value}
                    onDelete={() => {
                      setBranch((prev) => prev.filter((_, i) => i !== index));
                      setSelect((prev) => prev.filter((_, i) => i !== index));
                    }}
                  />
                ))}
              </Box>
            )}
            MenuProps={MenuProps}
          >
            {isSuccess &&
              (content || [])?.map(({ storeName, id }, index) => {
                return (
                  <MenuItem
                    key={index}
                    value={storeName}
                    onClick={() => {
                      let selected = [...select];
                      selected.push(id);
                      setSelect(selected);
                    }}
                  >
                    {storeName}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>
      </div>
    </ConfirmModal>
  );
};
